import type { DeanonymizationEvent } from '../../shared/types'

type DeanonData = DeanonymizationEvent['data']

export type DeanonUpdateCallback = () => void

type DeanonUpdateNotifier = {
  /**
   * Register a callback fired whenever deanon values change after the initial deanonymize() has
   * resolved. The callback receives no value — it is a trigger to re-evaluate (e.g. re-run playbook
   * selection). Returns an unsubscribe function.
   */
  subscribe: (callback: DeanonUpdateCallback) => () => void
  /**
   * Marks the initial deanonymize() as resolved. Batches handled before this only seed the
   * baseline; they do not trigger subscribers (that initial data is delivered through deanonymize()
   * itself).
   */
  markReady: () => void
  /**
   * Feed a deanon batch whose BE write has resolved. Once ready, subscribers are triggered once if
   * any provider's value changed since the last batch; unchanged batches are ignored.
   */
  handle: (data: DeanonData) => void
}

const getProviderKey = (item: DeanonData[number]): string =>
  'poweredBy' in item ? `${item.provider}:${item.poweredBy}` : item.provider

/**
 * Tracks the latest deanon value per provider and triggers subscribers only when a value changes.
 * It is fed from the BE-write acknowledgement, so the trigger always fires after the updated data
 * has been persisted — letting consumers re-query a BE that already has the new values.
 */
export const createDeanonUpdateNotifier = (
  onError: (error: unknown) => void
): DeanonUpdateNotifier => {
  const callbacks = new Set<DeanonUpdateCallback>()
  const lastSerializedByProvider = new Map<string, string>()
  let isReady = false

  return {
    subscribe: callback => {
      callbacks.add(callback)
      return () => {
        callbacks.delete(callback)
      }
    },
    markReady: () => {
      isReady = true
    },
    handle: data => {
      let hasChange = false
      data.forEach(item => {
        const serialized = JSON.stringify(item.data)
        if (lastSerializedByProvider.get(getProviderKey(item)) !== serialized) {
          lastSerializedByProvider.set(getProviderKey(item), serialized)
          hasChange = true
        }
      })

      if (!isReady || !hasChange || callbacks.size === 0) {
        return
      }

      callbacks.forEach(callback => {
        try {
          callback()
        } catch (error) {
          onError(error)
        }
      })
    },
  }
}
